import * as stylesImport from "./App.module.css";

export type DocItem = {
  readonly title: string;
  readonly detail: string;
};

type StylesMap = Readonly<Record<string, string>>;

export const QUICK_STEPS: ReadonlyArray<DocItem> = [
  { title: "1. Scaffold", detail: "Run npx costrix my-app to generate a complete project structure." },
  { title: "2. Install", detail: "Dependencies are installed automatically unless --no-install is used." },
  { title: "3. Build", detail: "Use npm run prod to run lint + typecheck + tests + production bundle." }
];

export const FEATURE_SECTIONS: ReadonlyArray<DocItem> = [
  { title: "CLI Flags", detail: "Use --no-install, --no-dev, and --help to control setup behavior." },
  { title: "Quality Guardrails", detail: "Strict TypeScript and strict ESLint are enabled by default." },
  { title: "Public Assets", detail: "Favicon and OG image are copied as binary-safe files during scaffold." }
];

const toStringMap = (source: Record<string, unknown>): StylesMap => {
  const entries = Object.entries(source).filter((entry): entry is [string, string] => typeof entry[1] === "string");
  return Object.fromEntries(entries);
};

export const resolveStyles = (source: unknown): StylesMap => {
  if (source === null || typeof source !== "object") {
    return {};
  }

  const directMap = toStringMap(source as Record<string, unknown>);
  if (Object.keys(directMap).length > 0) {
    return directMap;
  }

  const wrappedDefault = (source as { default?: unknown }).default;
  if (wrappedDefault !== null && typeof wrappedDefault === "object") {
    return toStringMap(wrappedDefault as Record<string, unknown>);
  }

  return {};
};

export const cls = (styles: StylesMap, key: string): string => styles[key] ?? key;

export const createEl = <K extends keyof HTMLElementTagNameMap>(
  doc: Document,
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] => {
  const element = doc.createElement(tag);
  if (className !== undefined && className.length > 0) {
    element.className = className;
  }
  if (text !== undefined && text.length > 0) {
    element.textContent = text;
  }
  return element;
};

export const buildDocItem = (doc: Document, styles: StylesMap, item: DocItem): HTMLElement => {
  const card = createEl(doc, "article", cls(styles, "docCard"));
  const title = createEl(doc, "h3", cls(styles, "docCardTitle"), item.title);
  const detail = createEl(doc, "p", cls(styles, "docCardDetail"), item.detail);
  card.append(title, detail);
  return card;
};

export const buildDocGrid = (doc: Document, styles: StylesMap, items: ReadonlyArray<DocItem>): HTMLElement => {
  const section = createEl(doc, "section", cls(styles, "docGrid"));
  const cards = items.map((item) => buildDocItem(doc, styles, item));
  section.append(...cards);
  return section;
};

export const buildCodePanel = (doc: Document, styles: StylesMap): HTMLElement => {
  const panel = createEl(doc, "div", cls(styles, "codePanel"));
  const heading = createEl(doc, "h2", cls(styles, "sectionTitle"), "Quick Start");
  const command = createEl(doc, "code", cls(styles, "mainCommand"), "npx costrix my-app");
  const lineTwo = createEl(doc, "code", cls(styles, "commandLine"), "cd my-app");
  const lineThree = createEl(doc, "code", cls(styles, "commandLine"), "npm run dev");
  panel.append(heading, command, lineTwo, lineThree);
  return panel;
};

export const buildHero = (doc: Document, styles: StylesMap): HTMLElement => {
  const hero = createEl(doc, "header", cls(styles, "hero"));
  const eyebrow = createEl(doc, "p", cls(styles, "eyebrow"), "Documentation");
  const title = createEl(doc, "h1", cls(styles, "title"), "Costrix Documentation");
  const subtitle = createEl(
    doc,
    "p",
    cls(styles, "subtitle"),
    "Everything starts with npx costrix. Scaffold, run, and ship a strict TypeScript codebase with confidence."
  );
  const docsButton = createEl(doc, "a", cls(styles, "docsButton"), "GitHub");
  docsButton.setAttribute("href", "https://github.com/kaustubh-vats/costrix");
  docsButton.setAttribute("target", "_blank");
  docsButton.setAttribute("rel", "noopener noreferrer");
  hero.append(eyebrow, title, subtitle, docsButton);
  return hero;
};

export const buildPage = (
  doc: Document,
  styles: StylesMap,
  steps: ReadonlyArray<DocItem> = QUICK_STEPS,
  sections: ReadonlyArray<DocItem> = FEATURE_SECTIONS
): HTMLElement => {
  const page = createEl(doc, "main", cls(styles, "page"));
  const backdropOne = createEl(doc, "div", cls(styles, "backdropOne"));
  const backdropTwo = createEl(doc, "div", cls(styles, "backdropTwo"));
  const shell = createEl(doc, "div", cls(styles, "shell"));

  const left = createEl(doc, "section", cls(styles, "leftColumn"));
  const right = createEl(doc, "section", cls(styles, "rightColumn"));

  const hero = buildHero(doc, styles);
  const stepHeading = createEl(doc, "h2", cls(styles, "sectionTitle"), "How It Works");
  const stepGrid = buildDocGrid(doc, styles, steps);
  left.append(hero, stepHeading, stepGrid);

  const featureHeading = createEl(doc, "h2", cls(styles, "sectionTitle"), "Reference");
  const featureGrid = buildDocGrid(doc, styles, sections);
  const codePanel = buildCodePanel(doc, styles);
  const footer = createEl(
    doc,
    "p",
    cls(styles, "footer"),
    "Need deeper guidance? Use npx costrix and follow the generated README."
  );
  right.append(codePanel, featureHeading, featureGrid, footer);

  shell.append(left, right);
  page.append(backdropOne, backdropTwo, shell);
  return page;
};

export const mountApp = (
  doc: Document = document,
  stylesSource: unknown = stylesImport
): HTMLElement => {
  const styles = resolveStyles(stylesSource);
  const root = createEl(doc, "div", cls(styles, "app"));
  root.setAttribute("data-costrix-root", "true");
  root.append(buildPage(doc, styles));
  doc.body.append(root);
  return root;
};

mountApp();
